import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommitImportInput, ImportEntityType, ImportJobDetailResponse, ImportJobResponse } from '@orion/contracts';
import { api, ApiError, getCsrfToken } from '../../lib/api-client.js';

export function useImportJobsQuery() {
  return useQuery({ queryKey: ['imports'], queryFn: () => api.get<ImportJobResponse[]>('/imports') });
}

export function useUploadImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, entityType }: { file: File; entityType: ImportEntityType }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('entityType', entityType);
      const csrf = getCsrfToken();
      const res = await fetch('/api/v1/imports', {
        method: 'POST',
        credentials: 'include',
        // No Content-Type here — the browser sets the multipart boundary itself.
        headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
        body: form,
      });
      const payload = await res.json();
      if (!res.ok) throw new ApiError(payload, res.status);
      return payload.data as ImportJobResponse;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['imports'] }),
  });
}

export function useValidateImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CommitImportInput }) => api.post<ImportJobDetailResponse>(`/imports/${id}/validate`, input),
    onSuccess: (_, { id }) => queryClient.invalidateQueries({ queryKey: ['imports', id] }),
  });
}

export function useCommitImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: CommitImportInput }) => {
      const csrf = getCsrfToken();
      const res = await fetch(`/api/v1/imports/${id}/commit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
        body: JSON.stringify(input),
      });
      const payload = await res.json();
      if (!res.ok) throw new ApiError(payload, res.status);
      return payload.data as ImportJobResponse;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['imports'] }),
  });
}

export function useImportJobQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['imports', id],
    queryFn: () => api.get<ImportJobDetailResponse>(`/imports/${id}`),
    enabled: Boolean(id),
  });
}

export function useRollbackImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/imports/${id}/rollback`, {}),
    onSuccess: (_, id) => queryClient.invalidateQueries({ queryKey: ['imports', id] }),
  });
}
